import React, { useState } from 'react';
import { X, AlertCircle, CheckCircle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { spacing, shadows, borderRadius } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Individual Toast Item Component
 *
 * Displays a single toast notification with optional expandable details.
 */
const ToastItem = ({ toast, onDismiss, colors }) => {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = toast.suggestions && toast.suggestions.length > 0;

  const getToastStyles = (type, expanded) => {
    const baseStyles = {
      display: 'flex',
      flexDirection: 'column',
      padding: spacing.lg,
      borderRadius: borderRadius.lg,
      boxShadow: shadows.md,
      minWidth: '320px',
      maxWidth: '500px',
      border: '1px solid',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: '0.875rem',
      animation: 'toastSlideIn 0.3s ease-out',
      transition: 'max-height 0.3s ease-out'
    };

    switch (type) {
      case 'error':
        return {
          ...baseStyles,
          background: 'hsl(0deg 100% 97%)',
          borderColor: 'hsl(0deg 70% 85%)',
          color: 'hsl(0deg 70% 30%)'
        };
      case 'success':
        return {
          ...baseStyles,
          background: 'hsl(142deg 76% 96%)',
          borderColor: 'hsl(142deg 76% 80%)',
          color: 'hsl(142deg 76% 25%)'
        };
      case 'info':
      default:
        return {
          ...baseStyles,
          background: colors.backgroundPanel,
          borderColor: colors.border,
          color: colors.foreground
        };
    }
  };

  const getIcon = (type) => {
    const iconProps = { size: 20 };

    switch (type) {
      case 'error':
        return <AlertCircle {...iconProps} style={{ flexShrink: 0, color: 'hsl(0deg 70% 50%)' }} />;
      case 'success':
        return <CheckCircle {...iconProps} style={{ flexShrink: 0, color: 'hsl(142deg 76% 40%)' }} />;
      case 'info':
      default:
        return <Info {...iconProps} style={{ flexShrink: 0, color: colors.foreground }} />;
    }
  };

  // Support both old string format and new detailed format
  const title = toast.title;
  const message = toast.message;
  const displayText = title ? `${title}: ${message}` : message;

  return (
    <div style={getToastStyles(toast.type, expanded)}>
      {/* Header row with icon, message, and close button */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: spacing.md
      }}>
        {getIcon(toast.type)}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          {title && (
            <strong style={{ fontWeight: 600 }}>{title}</strong>
          )}
          <span style={{ lineHeight: '1.4' }}>{message || displayText}</span>
        </div>
        <button
          onClick={() => onDismiss(toast.id)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: spacing.xs,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'inherit',
            opacity: 0.6,
            transition: 'opacity 0.2s',
            flexShrink: 0
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
          aria-label="Dismiss notification"
        >
          <X size={16} />
        </button>
      </div>

      {/* Expandable details section */}
      {hasDetails && (
        <>
          <button
            onClick={() => setExpanded(!expanded)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: `${spacing.sm} 0`,
              marginTop: spacing.xs,
              display: 'flex',
              alignItems: 'center',
              gap: spacing.xs,
              color: 'inherit',
              fontSize: '0.8125rem',
              fontWeight: 500,
              opacity: 0.8,
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {expanded ? 'Hide details' : 'Show details'}
          </button>

          {expanded && (
            <div style={{
              marginTop: spacing.sm,
              paddingTop: spacing.sm,
              borderTop: '1px solid',
              borderColor: toast.type === 'error' ? 'hsl(0deg 70% 90%)' :
                          toast.type === 'success' ? 'hsl(142deg 76% 85%)' :
                          colors.border,
              animation: 'detailsSlideIn 0.2s ease-out'
            }}>
              <div style={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                marginBottom: spacing.xs,
                opacity: 0.9
              }}>
                Suggestions:
              </div>
              <ul style={{
                margin: 0,
                paddingLeft: spacing.lg,
                fontSize: '0.8125rem',
                lineHeight: '1.5',
                opacity: 0.85
              }}>
                {toast.suggestions.map((suggestion, index) => (
                  <li key={index} style={{ marginBottom: spacing.xs }}>
                    {suggestion}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/**
 * Toast Component
 *
 * Displays notification toasts with different types (error, success, info).
 * Supports detailed error messages with expandable suggestions.
 */
const Toast = ({ toasts = [], onDismiss }) => {
  const { colors } = useTheme();

  if (toasts.length === 0) return null;

  return (
    <>
      <style>{`
        @keyframes toastSlideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes toastSlideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }

        @keyframes detailsSlideIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <div style={{
        position: 'fixed',
        top: spacing.xl,
        right: spacing.xl,
        zIndex: 9999,
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.md
      }}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            style={{ pointerEvents: 'auto' }}
          >
            <ToastItem toast={toast} onDismiss={onDismiss} colors={colors} />
          </div>
        ))}
      </div>
    </>
  );
};

export default Toast;
